import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  Alert,
  Grid,
  ColumnLayout,
  StatusIndicator,
  Badge,
  Form,
  FormField,
  Input
} from '@cloudscape-design/components';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const FamilySettingsView = ({ 
  currentUser,
  onFamilyCreated
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [familyName, setFamilyName] = useState('');
  const [familyInfo, setFamilyInfo] = useState(null);

  // 检查用户是否为家长且没有绑定家庭
  const isParentWithoutFamily = currentUser?.role === 'Parent' && (!currentUser?.family_id || currentUser?.family_id === '');

  // 获取家庭信息
  const fetchFamilyInfo = async () => {
    if (!currentUser?.family_id) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/families/${currentUser.family_id}/info`);
      setFamilyInfo(response.data);
    } catch (error) {
      console.error('获取家庭信息失败:', error);
    }
  };

  // 创建家庭
  const createFamily = async () => {
    if (!familyName.trim()) {
      setError('请输入家庭名称');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/families/create`, {
        name: familyName.trim(),
        creator_id: currentUser.id
      });

      setSuccess('家庭创建成功！');
      setFamilyName('');
      
      // 通知父组件家庭已创建，可能需要刷新用户信息
      if (onFamilyCreated) {
        onFamilyCreated(response.data);
      }

      // 刷新家庭信息
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('创建家庭失败:', error);
      setError(error.response?.data?.detail || '创建家庭失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 页面加载时获取家庭信息
  useEffect(() => {
    fetchFamilyInfo();
  }, [currentUser]);

  // 清除消息
  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <SpaceBetween size="l">
      {/* 错误提示 */}
      {error && (
        <Alert type="error" dismissible onDismiss={clearMessages}>
          {error}
        </Alert>
      )}

      {/* 成功提示 */}
      {success && (
        <Alert type="success" dismissible onDismiss={clearMessages}>
          {success}
        </Alert>
      )}

      {/* 页面标题 */}
      <Container
        header={
          <Header
            variant="h2"
            description="管理家庭设置和信息"
          >
            家庭设置
          </Header>
        }
      >
        <SpaceBetween size="l">
          {/* 用户信息显示 */}
          <Container header={<Header variant="h3">当前用户信息</Header>}>
            <Grid gridDefinition={[
              { colspan: { default: 12, s: 6 } },
              { colspan: { default: 12, s: 6 } }
            ]}>
              <SpaceBetween size="s">
                <Box display="flex" justifyContent="space-between">
                  <span>用户名:</span>
                  <Badge color="blue">{currentUser?.username || '未知'}</Badge>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <span>成员名称:</span>
                  <Badge color="green">{currentUser?.member_name || '未设置'}</Badge>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <span>用户角色:</span>
                  <Badge color={currentUser?.role === 'Parent' ? 'blue' : 'green'}>
                    {currentUser?.role === 'Parent' ? '家长' : '孩子'}
                  </Badge>
                </Box>
              </SpaceBetween>
              <SpaceBetween size="s">
                <Box display="flex" justifyContent="space-between">
                  <span>家庭ID:</span>
                  <Badge color={currentUser?.family_id ? 'green' : 'red'}>
                    {currentUser?.family_id || '未绑定'}
                  </Badge>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <span>金币余额:</span>
                  <Box display="flex" alignItems="center">
                    <span style={{ color: '#ff9900', marginRight: '4px' }}>🪙</span>
                    <Badge color="blue">{currentUser?.points || 0}</Badge>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <span>账户状态:</span>
                  <StatusIndicator type={currentUser ? 'success' : 'error'}>
                    {currentUser ? '已登录' : '未登录'}
                  </StatusIndicator>
                </Box>
              </SpaceBetween>
            </Grid>
          </Container>

          {/* 家庭设置区域 */}
          {isParentWithoutFamily ? (
            /* 家长且未绑定家庭 - 显示创建家庭表单 */
            <Container header={<Header variant="h3">创建家庭</Header>}>
              <SpaceBetween size="l">
                <Alert type="info">
                  <Box>
                    <Box fontWeight="bold">欢迎使用家庭任务管理系统！</Box>
                    <Box>您是家长用户，但还没有绑定家庭。请为您的家庭取一个名字，创建家庭后您就可以开始管理家庭任务了。</Box>
                  </Box>
                </Alert>

                <Form>
                  <SpaceBetween size="l">
                    <FormField 
                      label="家庭名称" 
                      stretch
                      description="为您的家庭取一个温馨的名字，比如：张家大院、幸福之家、快乐家庭等"
                    >
                      <Input
                        value={familyName}
                        onChange={({ detail }) => setFamilyName(detail.value)}
                        placeholder="请输入家庭名称"
                        disabled={loading}
                      />
                    </FormField>

                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        variant="primary"
                        onClick={createFamily}
                        loading={loading}
                        disabled={!familyName.trim()}
                      >
                        {loading ? '创建中...' : '创建家庭'}
                      </Button>
                    </Box>
                  </SpaceBetween>
                </Form>
              </SpaceBetween>
            </Container>
          ) : currentUser?.family_id ? (
            /* 已绑定家庭 - 显示家庭信息 */
            <Container header={<Header variant="h3">家庭信息</Header>}>
              <SpaceBetween size="l">
                <Alert type="success">
                  您已成功绑定家庭，可以正常使用家庭任务管理功能。
                </Alert>

                {familyInfo && (
                  <Grid gridDefinition={[
                    { colspan: { default: 12, s: 6 } },
                    { colspan: { default: 12, s: 6 } }
                  ]}>
                    <SpaceBetween size="s">
                      <Box display="flex" justifyContent="space-between">
                        <span>家庭名称:</span>
                        <Badge color="blue">{familyInfo.name || '未设置'}</Badge>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <span>家庭ID:</span>
                        <Badge color="grey">{familyInfo.id}</Badge>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <span>成员数量:</span>
                        <Badge color="green">{familyInfo.members?.length || 0} 人</Badge>
                      </Box>
                    </SpaceBetween>
                    <SpaceBetween size="s">
                      <Box>
                        <Box fontWeight="bold" marginBottom="xs">家庭成员:</Box>
                        {familyInfo.members && familyInfo.members.length > 0 ? (
                          <SpaceBetween size="xs">
                            {familyInfo.members.map((member, index) => (
                              <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
                                <span>{member.name || '未知成员'}</span>
                                <Badge color={member.role === 'Parent' ? 'blue' : 'green'}>
                                  {member.role === 'Parent' ? '家长' : '孩子'}
                                </Badge>
                              </Box>
                            ))}
                          </SpaceBetween>
                        ) : (
                          <Box color="text-body-secondary">暂无成员信息</Box>
                        )}
                      </Box>
                    </SpaceBetween>
                  </Grid>
                )}

                <Box display="flex" justifyContent="flex-end">
                  <Button onClick={fetchFamilyInfo}>
                    刷新家庭信息
                  </Button>
                </Box>
              </SpaceBetween>
            </Container>
          ) : (
            /* 其他情况 - 显示提示信息 */
            <Container header={<Header variant="h3">家庭状态</Header>}>
              <SpaceBetween size="l">
                <Alert type="warning">
                  <Box>
                    <Box fontWeight="bold">需要家长权限</Box>
                    <Box>
                      {!currentUser ? 
                        '请先登录系统。' : 
                        currentUser.role !== 'Parent' ? 
                          '只有家长用户可以创建和管理家庭。如果您是家长，请联系管理员更新您的角色权限。' :
                          '您的账户状态异常，请联系管理员。'
                      }
                    </Box>
                  </Box>
                </Alert>

                <Box display="flex" justifyContent="center">
                  <Button onClick={() => window.location.reload()}>
                    刷新页面
                  </Button>
                </Box>
              </SpaceBetween>
            </Container>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default FamilySettingsView;